import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

type Pattern = "pulse" | "oompah" | "tresillo" | "arpeggio" | "waltz";

// All patterns use the D major chord: D-F#-A.
const D_CHORD = ["D4", "F#4", "A4"];

const pulse: RhythmCell[] = [
  { hand: "L", on: true, pitches: D_CHORD, caption: "1" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "2" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "3" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "4" },
];

// Oom-Pah: root alone (Oom), then chord (Pa).
const oompah: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D3"], caption: "Oom" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "Pa" },
  { hand: "L", on: true, pitches: ["A2"], caption: "Oom" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "Pa" },
];

const tresillo: RhythmCell[] = [
  { hand: "L", on: true, pitches: D_CHORD, caption: "1" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "&-2" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "4" },
  { hand: "L", on: false, pitches: [], caption: "—" },
];

// Arpeggio LHMH: D (Low) - A (High) - F# (Middle) - A (High)
const arpeggio: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D3"], caption: "L" },
  { hand: "L", on: true, pitches: ["A4"], caption: "H" },
  { hand: "L", on: true, pitches: ["F#4"], caption: "M" },
  { hand: "L", on: true, pitches: ["A4"], caption: "H" },
  { hand: "L", on: true, pitches: ["D3"], caption: "L" },
  { hand: "L", on: true, pitches: ["A4"], caption: "H" },
  { hand: "L", on: true, pitches: ["F#4"], caption: "M" },
  { hand: "L", on: true, pitches: ["A4"], caption: "H" },
];

// Waltz: 3/4 — broken chord with one extra hit per bar
const waltz: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D3"], caption: "Oom" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "Pa" },
  { hand: "L", on: true, pitches: D_CHORD, caption: "Pa" },
];

export default function PianoFoundation14() {
  const { lesson, course, accent } = findLesson("piano-foundation", "14");
  const [pattern, setPattern] = useState<Pattern>("pulse");

  const grid =
    pattern === "pulse" ? pulse :
    pattern === "oompah" ? oompah :
    pattern === "tresillo" ? tresillo :
    pattern === "arpeggio" ? arpeggio : waltz;

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Same four rhythm patterns from Lesson 12 — but now in the left hand the unit isn't root+octave, it's a <strong>full chord</strong> (D major: D, F♯, A).</Highlight>
        <Highlight><strong>Pulse</strong> · <strong>Oom-Pah</strong> · <strong>Tresillo</strong> · <strong>Arpeggio</strong> — plus a fifth pattern for 3-beat songs: the <strong>Waltz</strong>.</Highlight>
        <Highlight>the most useful pattern is the arpeggio — broken into <strong>L-H-M-H</strong> (Low-High-Middle-High). For D major: D · A · F♯ · A.</Highlight>
        <Highlight>Switch the chord with the song's harmony (Tonic, Pre-Dominant, Dominant). Same rhythm pattern, different chord — that's how the LH "narrates" through the changes.</Highlight>
        <Highlight>Match pattern to song mood — pulse for simple, tresillo for groovy/dancey, arpeggio for ballads, waltz for 3-beat songs like Happy Birthday.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The upgrade" title="Same rhythms · now with full chords" accent={accent.hex}>
        <p>
          Lesson 12's left hand had a tiny vocabulary — root and octave only. This lesson
          expands the LH unit to a full chord. The chord is <strong>D major (D-F♯-A)</strong>{" "}
          as the demonstration chord, but the rhythm patterns transfer to any chord you'd
          play (Tonic, Pre-Dominant, Dominant from Lesson 13).
        </p>

        <KeyIdea>
          The right hand still plays the melody. The left hand still holds time. The only
          change is what gets struck on each LH hit — a single bass note becomes a three-note
          chord. The patterns themselves are identical to Lesson 12.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Try the five patterns" title="Same chord, five feels" accent={accent.hex}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setPattern("pulse")} className={pattern === "pulse" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Pulse</button>
          <button onClick={() => setPattern("oompah")} className={pattern === "oompah" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Oom-Pah</button>
          <button onClick={() => setPattern("tresillo")} className={pattern === "tresillo" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Tresillo</button>
          <button onClick={() => setPattern("arpeggio")} className={pattern === "arpeggio" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Arpeggio (LHMH)</button>
          <button onClick={() => setPattern("waltz")} className={pattern === "waltz" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Waltz · 3/4</button>
        </div>

        <RhythmGrid
          cells={grid}
          subdivisions={pattern === "tresillo" || pattern === "arpeggio" ? 2 : 1}
          beats={pattern === "waltz" ? 3 : 4}
          bpm={pattern === "waltz" ? 132 : 88}
          label={
            pattern === "pulse" ? "Pulse · D major chord on every beat" :
            pattern === "oompah" ? "Oom-Pah · root alone (Oom), then chord (Pa)" :
            pattern === "tresillo" ? "Tresillo · 3+3+2 with the chord" :
            pattern === "arpeggio" ? "Arpeggio · L-H-M-H (D · A · F♯ · A)" :
            "Waltz · 3/4 — Oom · Pa · Pa"
          }
        />
      </Insight>

      <Insight eyebrow="Pattern 1 · Pulse" title="The bedrock" accent={accent.hex}>
        <p>
          The starting point. Full chord struck on every beat. the first
          thing to do with any chord in the left hand is hold the pulse while the right hand
          plays the melody.
        </p>
      </Insight>

      <Insight eyebrow="Pattern 2 · Oom-Pah" title="Break the chord — root then top" accent={accent.hex}>
        <p>
          "Break up the chord". The pinky plays the bottom note (the root) — that's
          <strong> Oom</strong>. Then the rest of the chord (3rd and 5th) — that's
          <strong> Pa</strong>. <em>Oom · Pa · Oom · Pa</em>. The hand has motion now;
          the song has a slight bounce.
        </p>

        <Tip>
          The "Oom" can alternate too — pinky plays root D, then on the next "Oom" play A
          (the 5th in the bass) instead. That's the toggle variation that makes the bass
          itself walk while the chord above stays the same.
        </Tip>
      </Insight>

      <Insight eyebrow="Pattern 3 · Tresillo" title="With chords this time" accent={accent.hex}>
        <p>
          Same Latin/Afro-Cuban 3+3+2 pattern from Lesson 12, but each hit is now the full
          chord. It's tricky at first. Worth keeping in mind: <em>"Put some effort into it, it
          will come, it will take you a few days. It sounds really cool when you put it
          together."</em>
        </p>
      </Insight>

      <Insight eyebrow="Pattern 4 · Arpeggio (L-H-M-H)" title="the most useful pattern" accent={accent.hex}>
        <Pull>
          And then coming to my favourite left-hand pattern of them all — arpeggios.
        </Pull>

        <p>
          Break the chord into single notes. For D major (D-F♯-A), the order is:{" "}
          <strong>Low (D) · High (A) · Middle (F♯) · High (A)</strong> — the shorthand: L-H-M-H. The pattern loops; the chord changes underneath when the song
          asks for it.
        </p>

        <p>
          This pattern is the bedrock of ballad accompaniment. Listen to it under any slow
          piano song and you'll hear the same L-H-M-H gesture (or close cousins).
        </p>
      </Insight>

      <Insight eyebrow="Pattern 5 · Waltz · 3/4" title="For Happy Birthday and other 3-beat songs" accent={accent.hex}>
        <p>
          Some songs aren't in 4/4. Happy Birthday is in 3/4 — a <strong>waltz</strong>. To
          fit the pattern to three beats, take the broken-chord idea (Oom-Pa) and add one
          more <em>Pa</em>:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Oom · Pa · Pa  |  Oom · Pa · Pa
        </p>

        <p>
          Same hand mechanics as Oom-Pah; just one more upbeat per bar. Hum "Happy birthday
          to you" while you play and the rhythm sits perfectly under it.
        </p>
      </Insight>

      <Insight eyebrow="The practice" title="Bank the patterns — pull them out song by song" accent={accent.hex}>
        <Steps>
          <Step>
            Pick a melody you know well in the right hand. Mary Had a Little Lamb works for
            4/4; Happy Birthday works for the waltz.
          </Step>
          <Step>
            Hold the right-hand melody on autopilot. Cycle through the five LH patterns
            underneath. Two bars per pattern, switching at the bar line.
          </Step>
          <Step>
            Now bring in the chord changes from Lesson 13 (Tonic, Pre-Dominant, Dominant).
            Same LH pattern, but the chord changes when the melody calls for it.
          </Step>
          <Step>
            Match pattern to mood. <strong>Pulse</strong> for simple. <strong>Tresillo</strong>{" "}
            for groovy and dancey. <strong>Arpeggio</strong> for ballads. <strong>Waltz</strong>{" "}
            for 3/4 songs.
          </Step>
        </Steps>

        <Pull>
          If you equip yourself with these different styles and different patterns in
          the left hand, what's pretty much going to change will be the chords as per
          the song you're going to play and the melody will change. But the rhythm
          pattern, you're sort of banking it and trying to bring it out whenever you
          need to.
        </Pull>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-15"
        label="Lesson 15 · Accompany your Voice with Chords (Hand Ratios)"
        why="The LH now has rhythm patterns with chords. Next we add a key concept: the Hand Ratio — how often the LH hits compared to the RH — across a four-chord pop progression."
      />
    </LessonShell>
  );
}
